import type {
  WEventView,
  PlaceCardGuest,
  CardProjectPlaceCardDetailView,
  CardProjectPlaceCardDetailGuestCreateRequest,
  CardProjectPlaceCardDetailGuestUpdateRequest,
} from '@zola/svc-web-api-ts-client';
import ApiService from '../../util/api';

export const fetchPlacecardGuestList = ({
  projectUuid,
}: {
  projectUuid: string;
}): Promise<CardProjectPlaceCardDetailView> => {
  return ApiService.get(`/web-api/v2/card-project/${projectUuid}/place-card/details`);
};

export const createGuest = ({
  projectUuid,
  request,
}: {
  projectUuid: string;
  request: CardProjectPlaceCardDetailGuestCreateRequest;
}): Promise<CardProjectPlaceCardDetailView> => {
  return ApiService.post(
    `/web-api/v2/card-project/${projectUuid}/place-card/details/guest-list`,
    request
  );
};

export const updateGuest = ({
  projectUuid,
  request,
}: {
  projectUuid: string;
  request: CardProjectPlaceCardDetailGuestUpdateRequest;
}): Promise<CardProjectPlaceCardDetailView> => {
  if (!request.uuid) {
    throw new Error('missing request.uuid');
  }

  return ApiService.put(
    `/web-api/v2/card-project/${projectUuid}/place-card/details/guest-list/${request.uuid}`,
    request
  );
};

export const deleteGuest = ({
  projectUuid,
  guest,
}: {
  projectUuid: string;
  guest: PlaceCardGuest;
}): Promise<CardProjectPlaceCardDetailView> => {
  if (!guest.uuid) {
    throw new Error('missing guest.uuid');
  }

  return ApiService.delete(
    `/web-api/v2/card-project/${projectUuid}/place-card/details/guest-list/${guest.uuid}`
  );
};

export const getCustomizationValueUuids = (
  // @ts-ignore TODO: port Customization{Container, Controller} to typescript
  customizationValues: any // eslint-disable-line
): { nameKey: string | null; tableKey: string | null } => {
  if (!customizationValues) {
    return { nameKey: null, tableKey: null };
  }

  const nameKey =
    Object.keys(customizationValues).find(
      key => customizationValues?.[key]?.tag === 'PLACE_CARD_GUEST'
    ) || null;
  const tableKey =
    Object.keys(customizationValues).find(
      key => customizationValues?.[key]?.tag === 'PLACE_CARD_TABLE'
    ) || null;

  return { nameKey, tableKey };
};

export const uploadGuestListCSV = ({ file }: { file: File }): Promise<{ objectUuid: string }> => {
  const formData = new FormData();
  formData.append('attachment', file, file.name);

  return fetch('/web-api/v2/card-project/place-card/details/guest-list/upload-csv', {
    credentials: 'same-origin',
    method: 'POST',
    body: formData,
  })
    .then(ApiService.handleErrors)
    .then(response => response.json());
};

export const importGuestListCSV = ({
  projectUuid,
  objectUuid,
}: {
  projectUuid: string;
  objectUuid: string;
}): Promise<CardProjectPlaceCardDetailView> => {
  return ApiService.post(
    `/web-api/v2/card-project/${projectUuid}/place-card/details/guest-list/csv`,
    { objectUuid }
  );
};

export const exportGuestListCSV = ({ projectUuid }: { projectUuid: string }): Promise<string> => {
  return fetch(`/web-api/v2/card-project/${projectUuid}/place-card/details/guest-list/csv`, {
    credentials: 'same-origin',
    method: 'GET',
  })
    .then(ApiService.handleErrors)
    .then(response => response.text());
  return ApiService.get();
};

export const bulkDeleteGuestList = ({
  projectUuid,
  guestUuids,
}: {
  projectUuid: string;
  guestUuids: string[];
}): Promise<CardProjectPlaceCardDetailView> => {
  return ApiService.post(
    `/web-api/v2/card-project/${projectUuid}/place-card/details/guest-list/bulk-delete`,
    { guestUuids }
  );
};

export const getWeddingEvents = (): Promise<WEventView[]> => {
  return ApiService.get(`/web-api/v2/card-project/place-card/details/guest-list/wedding-events`);
};

export interface CSVImportParams {
  projectUuid: string;
  eventUuid?: string;
  grouping?: string;
  enforceRSVP?: boolean;
}
export const getPrefilledCSVTemplate = ({
  projectUuid,
  eventUuid,
  grouping,
  enforceRSVP,
}: CSVImportParams): Promise<{ text: string }> => {
  return ApiService.post(
    `/web-api/v2/card-project/${projectUuid}/place-card/details/guest-list/prefill-template`,
    {
      eventUuid,
      is_enforcing_rsvp_attending: !!enforceRSVP,
      place_card_guest_name_import_option: grouping || 'EVERYONE_SEPARATE',
      enforcing_rsvp_attending: !!enforceRSVP,
    }
  );
};

export const importGuestList = ({
  projectUuid,
  eventUuid,
  grouping,
  enforceRSVP,
}: CSVImportParams): Promise<CardProjectPlaceCardDetailView> => {
  return ApiService.post(
    `/web-api/v2/card-project/${projectUuid}/place-card/details/guest-list/import-zola-guests`,
    {
      eventUuid,
      is_enforcing_rsvp_attending: !!enforceRSVP,
      place_card_guest_name_import_option: grouping || 'EVERYONE_SEPARATE',
      enforcing_rsvp_attending: !!enforceRSVP,
    }
  );
};
